import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FormInput } from '../../components';
import { camelCaseToText } from '../../utils/camelCaseToText';
import { Client, ClientTypeOptions, EmploymentStatuses, GenderOptions, MaritalStatusOptions, TargetAudienceStatuses } from '../../interfaces/Client';
import { PricingPlans } from '../../interfaces/Employer';
import { RatingLevel } from '../../interfaces/Client';
import { useTranslation } from 'react-i18next';
import { APICore } from '../../utils/api/apiCore';
import { deleteClient } from '../../utils/api/clients';
import { CampaignTypes, SourceTypes } from '../business-dashboard/consts';
import globalErrorHandler from '../../utils/globalErrorHandler';
import Select from 'react-select';
interface AddCustomerProps {
    show: boolean;
    onHide: () => void;
    onAddClient: (newClient: Client, oldClient?: Client) => void;
    client: Client;
}

const EditClient = ({ show, onHide, onAddClient, client }: AddCustomerProps) => {
    const loggedInUser = new APICore().getLoggedInUser().user;
    const { t } = useTranslation('clients');
    const [currentClient, setCurrentClient] = useState<Client>(client);

    useEffect(() => {
        setCurrentClient(client);
    }, [client]);

    const onInputChange = (e: any) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        const name = e.target.name;

        if (name === 'isVIP' && !value) {
            setCurrentClient({
                ...currentClient,
                isVIP: false,
                vipDetails: '',
            });
            return;
        }

        if (name === 'notInterested' && !value) {
            setCurrentClient({
                ...currentClient,
                notInterested: false,
            });
            return;
        }

        if (name === 'isIrrelevant' && !value) {
            setCurrentClient({
                ...currentClient,
                isIrrelevant: false,
                irrelevantReason: '',
            });
            return;
        }

        if (name === 'sourceType') {
            setCurrentClient({
                ...currentClient,
                source: {
                    ...currentClient.source,
                    sourceType: value,
                },
            });
            return;
        }

        if (name === 'campaign') {
            setCurrentClient({
                ...currentClient,
                source: {
                    ...currentClient.source,
                    campaign: value,
                },
            });
            return;
        }

        if (name === 'campaignName') {
            setCurrentClient({
                ...currentClient,
                source: {
                    ...currentClient.source,
                    campaignName: value,
                },
            });
            return;
        }

        setCurrentClient({
            ...currentClient,
            [name]: value,
        });
    };

    const onSubmit = (newClient: Client) => {
        onAddClient(newClient, client);
    };

    const jotFormRatingOptions = Object.keys(RatingLevel)
        .filter((key) => !isNaN(Number(RatingLevel[key as keyof typeof RatingLevel])))
        .map((key: string, index: number) => ({
            label: t(key) || '',
            value: index,
        }));

    const isUserAllowedToDelete = loggedInUser.isAdmin || loggedInUser.roles.includes('support');

    const handleDeleteClick = async () => {
        if (!isUserAllowedToDelete) return;

        if (!client) return;

        try {
            const userAnswerToIsraeliId = prompt(
                `Are you sure you want to delete ${client.firstName} ${client.lastName}?\nPlease enter the client's Israeli ID number to confirm`,
            );
            if (!userAnswerToIsraeliId && client.idNumber) return;

            if (userAnswerToIsraeliId && userAnswerToIsraeliId !== client.idNumber) {
                alert('Wrong Israeli ID number');
                return;
            }

            await deleteClient(client._id, userAnswerToIsraeliId || '');
            alert('Client deleted successfully');
            onHide();
            window.location.href = '/';
        } catch (err) {
            globalErrorHandler(err);
        }
    };

    return (
        <>
            <Modal show={show} onHide={onHide} aria-labelledby="contained-modal-title-vcenter" centered>
                <Modal.Header className="bg-light" onHide={onHide} closeButton>
                    <Modal.Title className="m-0">{client ? 'Edit Client' : 'Add New Client'}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    <FormInput
                        // value={client?.firstName}
                        label="First Name"
                        type="text"
                        name="firstName"
                        placeholder="Enter first name"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.firstName}
                    />
                    <FormInput
                        label="Last Name"
                        type="text"
                        name="lastName"
                        placeholder="Enter last name"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.lastName}
                    />

                    <FormInput
                        label="Email Address"
                        type="email"
                        name="email"
                        placeholder="Enter email"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.email}
                    />
                    <FormInput
                        label="Phone"
                        type="text"
                        name="phoneNumber"
                        placeholder="Enter phone number"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.phoneNumber}
                    />
                    <FormInput
                        label="ID Number"
                        type="text"
                        name="idNumber"
                        maxLength={9}
                        placeholder="Enter israel ID number"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.idNumber}
                    />
                    <FormInput
                        label="ID Issue Date"
                        type="date"
                        name="idIssueDate"
                        placeholder="Enter ID issue date"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.idIssueDate ? currentClient.idIssueDate.toString().substring(0, 10) : ''}
                    />
                    <Form.Group className="mb-3">
                        <Form.Label>Gender</Form.Label>
                        <Select
                            options={GenderOptions.map((type) => ({
                                label: camelCaseToText(type) || '',
                                value: type,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        gender: selectedOption.value,
                                    });
                                }
                            }}
                            value={
                                currentClient.gender
                                    ? {
                                          label: camelCaseToText(currentClient.gender) || '',
                                          value: currentClient.gender,
                                      }
                                    : null
                            }
                            placeholder="Select gender"
                        />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Marital Status</Form.Label>
                        <Select
                            options={MaritalStatusOptions.map((type) => ({
                                label: camelCaseToText(type) || '',
                                value: type,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        maritalStatus: selectedOption.value,
                                    });
                                }
                            }}
                            value={
                                currentClient.maritalStatus
                                    ? {
                                          label: camelCaseToText(currentClient.maritalStatus) || '',
                                          value: currentClient.maritalStatus,
                                      }
                                    : null
                            }
                            placeholder="Select marital status"
                        />
                    </Form.Group>
                    <FormInput
                        label="Date Of Birth"
                        type="date"
                        name="dateOfBirth"
                        placeholder="Enter date of birth"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.dateOfBirth ? currentClient.dateOfBirth.toString().substring(0, 10) : ''}
                    />
                    {/*select forminput for editing employmentStatus with the options of  employmentStatus from the client.ts*/}

                    <Form.Group className="mb-3">
                        <Form.Label>Source Type</Form.Label>
                        <Select
                            options={Object.values(SourceTypes).map((source) => ({
                                label: camelCaseToText(source) || '',
                                value: source,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        source: {
                                            ...currentClient.source,
                                            sourceType: selectedOption.value,
                                        },
                                    });
                                }
                            }}
                            value={
                                currentClient.source?.sourceType
                                    ? {
                                          label: camelCaseToText(currentClient.source.sourceType) || '',
                                          value: currentClient.source.sourceType,
                                      }
                                    : null
                            }
                            placeholder="Select source type"
                        />
                    </Form.Group>

                    {currentClient.source?.sourceType === SourceTypes.campaign ? (
                        <Form.Group className="mb-3">
                            <Form.Label>Campaign</Form.Label>
                            <Select
                                options={Object.values(CampaignTypes).map((campaign) => ({
                                    label: camelCaseToText(campaign) || '',
                                    value: campaign,
                                }))}
                                onChange={(selectedOption: any) => {
                                    if (selectedOption) {
                                        setCurrentClient({
                                            ...currentClient,
                                            source: {
                                                ...currentClient.source,
                                                campaign: selectedOption.value,
                                            },
                                        });
                                    }
                                }}
                                value={
                                    currentClient.source?.campaign
                                        ? {
                                              label: camelCaseToText(currentClient.source.campaign) || '',
                                              value: currentClient.source.campaign,
                                          }
                                        : null
                                }
                                placeholder="Select campaign"
                            />
                        </Form.Group>
                    ) : null}

                    <FormInput
                        label="Campaign Name"
                        type="text"
                        name="campaignName"
                        placeholder="Enter campaign name"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.source.campaignName}
                    />

                    <Form.Group className="mb-3">
                        <Form.Label>Employment Status</Form.Label>
                        <Select
                            options={EmploymentStatuses.map((status) => ({
                                label: camelCaseToText(status) || '',
                                value: status,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        employmentStatus: selectedOption.value,
                                    });
                                }
                            }}
                            value={
                                currentClient.employmentStatus
                                    ? {
                                          label: camelCaseToText(currentClient.employmentStatus) || '',
                                          value: currentClient.employmentStatus,
                                      }
                                    : null
                            }
                            placeholder="Select employment status"
                        />
                    </Form.Group>

                    <FormInput
                        label="Monthly Salary"
                        type="number"
                        name="monthlySalary"
                        placeholder="Enter monthly salary"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.monthlySalary}
                    />

                    <FormInput
                        // value={currentClient.firstName}
                        label="Retirement Age"
                        type="number"
                        name="retirementAge"
                        placeholder="If needed, enter the retirement age"
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                        value={currentClient.retirementAge}
                    />
                    <Form.Group className="mb-3">
                        <Form.Label>Client Type</Form.Label>
                        <Select
                            options={ClientTypeOptions.map((type) => ({
                                label: camelCaseToText(type) || '',
                                value: type,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        clientType: selectedOption.value,
                                    });
                                }
                            }}
                            value={
                                currentClient.clientType
                                    ? {
                                          label: camelCaseToText(currentClient.clientType) || '',
                                          value: currentClient.clientType,
                                      }
                                    : null
                            }
                            placeholder="Select client type"
                        />
                    </Form.Group>
                    <FormInput
                        label="VIP"
                        type="checkbox"
                        name="isVIP"
                        checked={currentClient.isVIP}
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                    />

                    <FormInput
                        label="Not Interested"
                        type="checkbox"
                        name="notInterested"
                        checked={currentClient.notInterested}
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                    />

                    <FormInput
                        label="Unsubscribed from bot messaging"
                        type="checkbox"
                        name="unsubscribedFromBotMessaging"
                        checked={currentClient.unsubscribedFromBotMessaging}
                        containerClass={'mb-3'}
                        onChange={onInputChange}
                    />

                    {currentClient.isVIP && (
                        <FormInput
                            label="VIP Details"
                            type="text"
                            name="vipDetails"
                            placeholder="Enter VIP details"
                            containerClass={'mb-3'}
                            onChange={onInputChange}
                            value={currentClient.vipDetails}
                        />
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label>Target Audience Status</Form.Label>
                        <Select
                            options={Object.keys(TargetAudienceStatuses).map((status) => ({
                                label: camelCaseToText(status) || '',
                                value: status,
                            }))}
                            onChange={(selectedOption: any) => {
                                if (selectedOption) {
                                    setCurrentClient({
                                        ...currentClient,
                                        targetAudienceStatus: selectedOption.value,
                                    });
                                }
                            }}
                            value={
                                currentClient.targetAudienceStatus
                                    ? {
                                          label: camelCaseToText(currentClient.targetAudienceStatus) || '',
                                          value: currentClient.targetAudienceStatus,
                                      }
                                    : null
                            }
                            placeholder="Select target audience status"
                        />
                    </Form.Group>

                    {currentClient.targetAudienceStatus === TargetAudienceStatuses.irrelevantClient && (
                        <FormInput
                            label="Irrelevant Reason"
                            type="text"
                            name="irrelevantReason"
                            placeholder="Enter irrelevant reason"
                            containerClass={'mb-3'}
                            onChange={onInputChange}
                            value={currentClient.irrelevantReason}
                        />
                    )}

                    <div className="d-flex justify-content-between flex-row-reverse">
                        <div className="text-end">
                            <Button variant="success" type="submit" className="waves-effect waves-light me-1" onClick={() => onSubmit(currentClient)}>
                                Save
                            </Button>
                        </div>
                        <div className="text-end">
                            <Button
                                variant="danger"
                                className="waves-effect waves-light me-1"
                                disabled={!isUserAllowedToDelete}
                                onClick={handleDeleteClick}
                            >
                                Delete Client
                            </Button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default EditClient;
